import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  crearMantenimiento,
  actualizarMantenimiento,
  agregarDetalleMantenimiento,
  listarDetallesMantenimiento,
  listarAlmacen,
  actualizarVehiculo,
  actualizarMaquinaria,
  actualizarUtillaje,
  borrarMantenimiento,
  borrarDetalleMantenimiento,
} from "../api";
import "../styles.css";
import "./ModalMantenimiento.css";
import { toast } from "react-toastify";

function ModalCrearMantenimiento({
  onClose,
  onGuardar,
  mantenimiento,
  vehiculos = [],
  maquinaria = [],
  utillaje = [],
}) {

  const [tipoEquipo, setTipoEquipo] = useState(
    mantenimiento?.vehiculo?.id
      ? "vehiculo"
      : mantenimiento?.maquinaria?.id
      ? "maquinaria"
      : mantenimiento?.utillaje?.id
      ? "utillaje"
      : ""
  );

  const [equipoId, setEquipoId] = useState(
    mantenimiento?.vehiculo?.id ||
      mantenimiento?.maquinaria?.id ||
      mantenimiento?.utillaje?.id ||
      ""
  );

  const [descripcion, setDescripcion] = useState(mantenimiento?.descripcion || "");
  const [observaciones, setObservaciones] = useState(mantenimiento?.observaciones || "");
  const [detalles, setDetalles] = useState([]);
  const [almacenItems, setAlmacenItems] = useState([]);
  const [nuevoDetalle, setNuevoDetalle] = useState({ almacen_id: "", cantidad_usada: 1 });
  const [busquedaEquipo, setBusquedaEquipo] = useState("");
  const [actualizarKmHoras, setActualizarKmHoras] = useState(false);
  const [kmHorasActual, setKmHorasActual] = useState("");

  useEffect(() => {
    listarAlmacen().then((items) => setAlmacenItems(items || []));
  }, []);

  useEffect(() => {
    if (!mantenimiento?.id || !almacenItems.length) return;
    listarDetallesMantenimiento(mantenimiento.id).then((det) => {
      const detallesCompletos = (det || []).map((d) => {
        const itemRelacionado = almacenItems.find((i) => i.id === d.almacen_id);
        return { ...d, item: itemRelacionado, idEnBackend: true };
      });
      setDetalles(detallesCompletos);
    });
  }, [mantenimiento, almacenItems]);

  useEffect(() => {
    if (!equipoId) {
      setKmHorasActual("");
      return;
    }
    let valor = "";
    if (tipoEquipo === "vehiculo") {
      valor = vehiculos.find((v) => v.id === Number(equipoId))?.km_actual ?? "";
    } else if (tipoEquipo === "maquinaria") {
      valor = maquinaria.find((m) => m.id === Number(equipoId))?.horas_actuales ?? "";
    } else if (tipoEquipo === "utillaje") {
      valor = utillaje.find((u) => u.id === Number(equipoId))?.horas_actuales ?? "";
    }
    setKmHorasActual(valor);
  }, [equipoId, tipoEquipo, vehiculos, maquinaria, utillaje]);

  const equiposFiltrados = useMemo(() => {
    const lista =
      tipoEquipo === "vehiculo"
        ? vehiculos
        : tipoEquipo === "maquinaria"
        ? maquinaria
        : tipoEquipo === "utillaje"
        ? utillaje
        : [];
    if (!busquedaEquipo) return lista || [];
    const texto = busquedaEquipo.toLowerCase();
    return (lista || []).filter(
      (e) =>
        (e?.nombre || "").toLowerCase().includes(texto) ||
        (e?.matricula || "").toLowerCase().includes(texto)
    );
  }, [tipoEquipo, busquedaEquipo, vehiculos, maquinaria, utillaje]);

  const itemsCompatibles = useMemo(() => {
    if (!equipoId || !tipoEquipo) return [];
    return (almacenItems || []).filter((item) => {
      if (tipoEquipo === "vehiculo")
        return item.vehiculos_compatibles?.some((v) => v.id === Number(equipoId));
      if (tipoEquipo === "maquinaria")
        return item.maquinarias_compatibles?.some((m) => m.id === Number(equipoId));
      if (tipoEquipo === "utillaje")
        return item.utillajes_compatibles?.some((u) => u.id === Number(equipoId));
      return false;
    });
  }, [almacenItems, equipoId, tipoEquipo]);

  const calcularStockDisponible = (itemId) => {
    const item = (almacenItems || []).find((i) => i.id === Number(itemId));
    return item ? item.cantidad : 0;
  };

  const handleAgregarDetalle = async () => {
    const itemId = Number(nuevoDetalle.almacen_id);
    const cantidad = Number(nuevoDetalle.cantidad_usada);
    if (!itemId) return toast.error("Selecciona un ítem válido");
    if (!cantidad || cantidad <= 0) return toast.error("Ingresa una cantidad válida");

    const item = almacenItems.find((i) => i.id === itemId);
    if (!item) return toast.error("Ítem no encontrado en el almacén");
    if (cantidad > item.cantidad) return toast.error(`Stock insuficiente: solo ${item.cantidad} disponible`);

    if (mantenimiento?.id) {
      try {
        const detalleGuardado = await agregarDetalleMantenimiento(mantenimiento.id, itemId, cantidad);
        setDetalles((prev) => [...prev, { ...detalleGuardado, item, idEnBackend: true }]);
      } catch (err) {
        console.error("Error agregando detalle al backend:", err);
        return toast.error("Error añadiendo detalle al servidor");
      }
    } else {
      setDetalles((prev) => [...prev, { id: Date.now(), almacen_id: itemId, cantidad_usada: cantidad, item, idEnBackend: false }]);
    }

    setAlmacenItems((prev) => prev.map((i) => i.id === itemId ? { ...i, cantidad: i.cantidad - cantidad } : i));
    setNuevoDetalle({ almacen_id: "", cantidad_usada: 1 });
  };

  const handleEliminarDetalle = async (detalleId) => {
    if (!window.confirm("¿Eliminar este detalle?")) return;

    const detalle = detalles.find((d) => d.id === detalleId);
    if (!detalle) return;

    setAlmacenItems((prev) => prev.map((i) => i.id === detalle.almacen_id ? { ...i, cantidad: i.cantidad + Number(detalle.cantidad_usada) } : i));

    if (detalle.idEnBackend && mantenimiento?.id) {
      try {
        await borrarDetalleMantenimiento(mantenimiento.id, detalle.id);
      } catch (err) {
        console.error("Error eliminando detalle del backend:", err);
      }
    }

    setDetalles((prev) => prev.filter((d) => d.id !== detalleId));
  };

  const handleGuardar = async () => {
    if (!descripcion || !equipoId || !tipoEquipo) {
      toast.error("Completa descripción y equipo");
      return;
    }

    try {
      const valorNumerico = kmHorasActual !== "" ? Number(kmHorasActual) : null;

      const payload = {
        descripcion,
        observaciones,
        km_actual: tipoEquipo === "vehiculo" ? valorNumerico : undefined,
        horas_actuales: tipoEquipo !== "vehiculo" ? valorNumerico : undefined,
        actualizarKmHoras,
      };

      if (tipoEquipo === "vehiculo") payload.vehiculo_id = Number(equipoId);
      if (tipoEquipo === "maquinaria") payload.maquinaria_id = Number(equipoId);
      if (tipoEquipo === "utillaje") payload.utillaje_id = Number(equipoId);

      let mantenimientoRes;
      if (mantenimiento?.id) {
        mantenimientoRes = await actualizarMantenimiento(mantenimiento.id, payload);
      } else {
        mantenimientoRes = await crearMantenimiento(payload);
      }

      const mantenimientoId = mantenimiento?.id || mantenimientoRes.id;

      const detallesActualizados = [];
      for (const d of detalles) {
        if (!d.idEnBackend) {
          const detalleGuardado = await agregarDetalleMantenimiento(
            mantenimientoId,
            d.almacen_id,
            d.cantidad_usada
          );
          detallesActualizados.push({ ...d, id: detalleGuardado.id, idEnBackend: true });
        } else {
          detallesActualizados.push(d);
        }
      }
      setDetalles(detallesActualizados);

      onGuardar(mantenimientoRes);
      onClose();
    } catch (err) {
      console.error("Error guardando mantenimiento:", err);
      toast.error("Error al guardar mantenimiento");
    }
  };

  const handleEliminarMantenimiento = async () => {
    if (!mantenimiento?.id) return;
    if (!window.confirm("¿Eliminar este mantenimiento?")) return;

    setAlmacenItems((prev) =>
      prev.map((i) => {
        const detalle = detalles.find((d) => d.almacen_id === i.id);
        if (detalle) return { ...i, cantidad: i.cantidad + Number(detalle.cantidad_usada) };
        return i;
      })
    );

    setDetalles([]);

    try {
      await borrarMantenimiento(mantenimiento.id);
      onGuardar(null);
      onClose();
    } catch (err) {
      console.error("Error eliminando mantenimiento:", err);
      toast.error("Error al eliminar mantenimiento");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-modern">
        <button className="modal-close-button" onClick={onClose}>✖</button>
        <h2 className="modal-title">{mantenimiento ? "Editar mantenimiento" : "Crear mantenimiento"}</h2>

        <div className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>Tipo de equipo</label>
              <select value={tipoEquipo} onChange={(e) => { setTipoEquipo(e.target.value); setEquipoId(""); }}>
                <option value="">-- Selecciona tipo --</option>
                <option value="vehiculo">Vehículo</option>
                <option value="maquinaria">Maquinaria</option>
                <option value="utillaje">Utillaje</option>
              </select>
            </div>

            {tipoEquipo && (
              <div className="form-group">
                <label>Equipo</label>
                <input type="text" placeholder="Buscar..." value={busquedaEquipo} onChange={(e) => setBusquedaEquipo(e.target.value)} />
                <select value={equipoId || ""} onChange={(e) => setEquipoId(e.target.value)}>
                  <option value="">-- Selecciona --</option>
                  {equiposFiltrados.map((e) => (
                    <option key={e.id} value={e.id}>{e.nombre || "Desconocido"} ({e.matricula || "-"})</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {tipoEquipo && (
            <div className="form-group">
              <label>
                <input type="checkbox" checked={actualizarKmHoras} onChange={(e) => setActualizarKmHoras(e.target.checked)} />
                Actualizar último mantenimiento también
              </label>
              <input type="number" placeholder={tipoEquipo === "vehiculo" ? "Km actuales..." : "Horas actuales..."} value={kmHorasActual || ""} onChange={(e) => setKmHorasActual(e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label>Descripción</label>
            <textarea value={descripcion || ""} onChange={(e) => setDescripcion(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Observaciones</label>
            <textarea value={observaciones || ""} onChange={(e) => setObservaciones(e.target.value)} />
          </div>

          <div className="detalles-section">
            <h3>Detalles</h3>
            {!detalles.length ? (
              <p className="no-resultados">No hay ítems añadidos.</p>
            ) : (
              <ul className="detalles-lista">
                {detalles.map((d) => (
                  <li key={d.id}>
                    <span>{d.item?.nombre || "Ítem"} - {d.cantidad_usada}</span>
                    <button type="button" className="botonEliminarMini" onClick={() => handleEliminarDetalle(d.id)}>❌</button>
                  </li>
                ))}
              </ul>
            )}

            <div className="detalles-add">
              <div className="select-item">
                <Select
                  options={itemsCompatibles.map((i) => ({ value: i.id, label: `${i.nombre} (Stock: ${calcularStockDisponible(i.id)})` }))}
                  value={
                    nuevoDetalle.almacen_id
                      ? itemsCompatibles.map((i) => ({ value: i.id, label: `${i.nombre} (Stock: ${calcularStockDisponible(i.id)})` }))
                        .find(opt => opt.value === Number(nuevoDetalle.almacen_id)) || null
                      : null
                  }
                  onChange={(selected) => setNuevoDetalle((prev) => ({ ...prev, almacen_id: selected?.value || "" }))}
                  placeholder="Selecciona ítem..."
                  isClearable
                />
              </div>

              <input type="number" min={1} value={nuevoDetalle.cantidad_usada} onChange={(e) => setNuevoDetalle({ ...nuevoDetalle, cantidad_usada: e.target.value })} placeholder="Cantidad" />
              <button type="button" onClick={handleAgregarDetalle}>Añadir</button>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="botonPrimario" onClick={handleGuardar}>Guardar</button>
            <button type="button" className="botonSecundario" onClick={onClose}>Cancelar</button>
            {mantenimiento && (
              <button type="button" className="botonEliminar" onClick={handleEliminarMantenimiento}>Eliminar</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalCrearMantenimiento;
